import ExcelJS from "exceljs";
import * as FileSaver from "file-saver";

const offsetRowHeight = 1.1;
const offsetColumnWidth = 1.24;

const primaryColor = "5175B9";
const borderColor = "000000";

export const fillColors: { [property: string]: ExcelJS.Fill } = {
  primary: {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: primaryColor },
  },
  secondary: {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFFFFF" },
  },
  gray: {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "D9D9D9" },
  },
};

export const fonts: { [property: string]: Partial<ExcelJS.Font> } = {
  InfoTitle: { name: "Arial", size: 14, italic: true },
  Header: { name: "Arial", size: 14, bold: true },
  Title: { name: "Arial", size: 12, italic: true },
  Text: { name: "Arial", size: 12 },
  TextBold: { name: "Arial", size: 12, bold: true },
  FooterTitle: {
    name: "Arial",
    size: 14,
    italic: true,
  },
  FooterValue: {
    name: "Arial",
    size: 14,
    bold: true,
  },
};

export const alignments: { [property: string]: Partial<ExcelJS.Alignment> } = {
  Middle: { vertical: "middle" },
  MiddleAndCenter: { vertical: "middle", horizontal: "center" },
  MiddleAndLeft: { vertical: "middle", horizontal: "left" },
  MiddleAndRight: { vertical: "middle", horizontal: "right" },
};

export const borders = {
  allThin: createBordersStyle("thin", "thin", "thin", "thin"),
  allThinWhite: createBordersStyle("thin", "thin", "thin", "thin", "FFFFFF"),
  thinTop: createBordersStyle("thin", "thick", "thick", "thick"),
  thinBottom: createBordersStyle("thick", "thick", "thin", "thick"),

  allThick: createBordersStyle("thick", "thick", "thick", "thick"),
  thickTop: createBordersStyle("thick", "thin", "thin", "thin"),
  thickTopBottom: createBordersStyle("thick", "thin", "thick", "thin"),
  thickRight: createBordersStyle("thin", "thick", "thin", "thin"),
  thickUpperRightCorner: createBordersStyle("thick", "thick", "thin", "thin"),
  thickRightAndLef: createBordersStyle("thin", "thick", "thin", "thick"),
  thickLeft: createBordersStyle("thin", "thin", "thin", "thick"),
  thickUpperLeftCorner: createBordersStyle("thick", "thin", "thin", "thick"),
  thickLowerLeftCorner: createBordersStyle("thin", "thin", "thick", "thick"),
  thickBottom: createBordersStyle("thin", "thin", "thick", "thin"),

  allDottedRed: createBordersStyle(
    "dotted",
    "dotted",
    "dotted",
    "dotted",
    "FF0000"
  ),
};

class ExcelService {
  private workbook: ExcelJS.Workbook;

  constructor() {
    this.workbook = new ExcelJS.Workbook();
    this.workbook.creator = "rubk.faturamento";
    this.workbook.created = new Date();
  }

  public createWorksheet(name: string): ExcelWorksheet {
    return new ExcelWorksheet(this.workbook, name);
  }

  public async saveAs(name: string) {
    const blob = await this.convertToBlob();
    const fileName = name.replace(/\s/g, "");

    FileSaver.saveAs(blob, fileName);
  }

  private async convertToBlob(): Promise<Blob> {
    const blobType =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    return this.workbook.xlsx.writeBuffer().then((data) => {
      return new Blob([data], { type: blobType });
    });
  }

  public async readTabsFromFile(file: File): Promise<ExcelJS.Worksheet[]> {
    const buffer = await file.arrayBuffer();
    await this.workbook.xlsx.load(buffer);
    return this.workbook.worksheets;
  }
}

export class ExcelWorksheet {
  private workbook: ExcelJS.Workbook;
  private worksheet: ExcelJS.Worksheet;

  constructor(workbook: ExcelJS.Workbook, name: string) {
    this.workbook = workbook;
    this.worksheet = workbook.addWorksheet(name, {
      // create new sheet with pageSetup settings for A4 - landscape
      pageSetup: { paperSize: 9, orientation: "landscape" },
    });
  }

  getCell(cell: string): ExcelJS.Cell {
    return this.worksheet.getCell(cell);
  }

  getColumn(columnIndex: number): ExcelJS.Column {
    return this.worksheet.getColumn(columnIndex);
  }

  getRow(rowIndex: number): ExcelJS.Row {
    return this.worksheet.getRow(rowIndex);
  }

  mergeCells(cell: string) {
    this.worksheet.mergeCells(cell);
  }

  cell(cell: string) {
    const cellComponent = this.getCell(cell);

    return {
      ref: cellComponent,
      set: (value, style?: Partial<ExcelJS.Style>) => {
        cellComponent.value = value;
        if (style) this.setCellFormat(cellComponent, style);
      },
      format: (style: Partial<ExcelJS.Style>) => {
        this.setCellFormat(cellComponent, style);
      },
      setFormula: (formula: string, result: number = 0) => {
        cellComponent.value = { formula, result } as any;
      },
      setBorder: (border: Partial<ExcelJS.Borders>) => {
        cellComponent.border = border;
      },
    };
  }

  private setCellFormat(
    cell: ExcelJS.Cell,
    { font, alignment, numFmt, border, fill }: Partial<ExcelJS.Style>
  ) {
    if (font) cell.font = font;
    if (alignment) cell.alignment = alignment;
    if (numFmt) cell.numFmt = numFmt;
    if (border) cell.border = border;
    if (fill) cell.fill = fill;
  }

  setColumnWidth(columnIndex: number, width: number) {
    this.worksheet.getColumn(columnIndex).width = width * offsetColumnWidth;
  }

  setRowHeight(rowIndex: number, height: number) {
    this.worksheet.getRow(rowIndex).height = height * offsetRowHeight;
  }

  addImage(
    image: {
      base64;
      extension: "jpeg" | "png" | "gif";
      width: number;
      height: number;
    },
    column: number,
    row: number
  ): number {
    const imageId = this.workbook.addImage({
      base64: image.base64,
      extension: image.extension,
    });

    this.worksheet.addImage(imageId, {
      tl: { col: column, row },
      ext: { width: image.width, height: image.height },
    });

    return imageId;
  }
}

export function createBordersStyle(
  top?: ExcelJS.BorderStyle,
  right?: ExcelJS.BorderStyle,
  bottom?: ExcelJS.BorderStyle,
  left?: ExcelJS.BorderStyle,
  color: string = borderColor
): Partial<ExcelJS.Borders> {
  let borders: any = {};
  if (top) borders.top = createBorderStyle(top, color);
  if (right) borders.right = createBorderStyle(right, color);
  if (bottom) borders.bottom = createBorderStyle(bottom, color);
  if (left) borders.left = createBorderStyle(left, color);

  return borders;
}

export function createBorderStyle(
  style: ExcelJS.BorderStyle,
  color: string = borderColor
) {
  return { style, color: { argb: color } };
}

export function getColumnLetter(index: number): string {
  const alphabet = [
    "A",
    "B",
    "C",
    "D",
    "E",
    "F",
    "G",
    "H",
    "I",
    "J",
    "K",
    "L",
    "M",
    "N",
    "O",
    "P",
    "Q",
    "R",
    "S",
    "T",
    "U",
    "V",
    "W",
    "X",
    "Y",
    "Z",
  ];
  return alphabet[index];
}

export default ExcelService;
